import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Fingerprint,
  Calendar,
  Briefcase,
  FileText,
  MapPin,
  AlertCircle,
  HelpCircle,
  Database,
} from 'lucide-react';
import { motion } from 'framer-motion';
import Swal from 'sweetalert2';
import { AbsenLuarPhotoModal } from './components/absen-luar/AbsenLuarPhotoModal';
import AbsenCard from './components/AbsenCard';
import RekapanCard from './components/RekapanCard';

// ==================== KONSTANTA ====================
const MAX_DISTANCE_METERS = 50;
const LOCATION_STAY_MAX_DISTANCE_METERS = 150;
const SYNC_RETRY_LIMIT = 3;
const SYNC_RETRY_INTERVAL_MS = 60000;
const LOCAL_RIWAYAT_KEY = 'riwayat_absen';
const LOCAL_ABSEN_SERVER_KEY = 'absen_server';
const LOCAL_JENIS_IJIN_KEY = 'jenis_ijin_list';
const LOCAL_KARYAWAN_PENGGANTI_KEY = 'karyawan_pengganti_list';
const LOCAL_LOCATION_MONITOR_KEY = 'absen_location_monitor';

const OFFICE_LOCATIONS = [
  { name: "JNE Cilacap", latitude: -7.703791784344155, longitude: 109.02706575208764 },
  { name: "KP Tritih", latitude: -7.6436984059273625, longitude: 109.03945539274153 },
  { name: "KP Jeruklegi", latitude: -7.622739973947992, longitude: 109.02027662866101 },
  { name: "KP Jeruklegi", latitude: -7.623527514340614, longitude: 109.01967888209312 },
  { name: "Pickup Jambusari", latitude: -7.570450003947906, longitude: 109.0257079820926 },
  { name: "KP Bantarsari", latitude: -7.553852092912492, longitude: 108.88562791441512 },
  { name: "KP Bantarsari", latitude: -7.556101357393882, longitude: 108.88581543810471 },
  { name: "KP Kawunganten", latitude: -7.597121637889708, longitude: 108.92088169558562 },
  { name: "KP Kawunganten", latitude: -7.596978029880988, longitude: 108.92066665149882 },
  { name: "KP Sidareja", latitude: -7.485411168876285, longitude: 108.8010667954 },
  { name: "KP Cimanggu", latitude: -7.356556746125122, longitude: 108.84221591667256 },
  { name: "KP Majenang", latitude: -7.298644648842441, longitude: 108.76225156021464 },
  { name: "KP Majenang", latitude: -7.299063, longitude: 108.761253 },
  { name: "KP Kesugihan", latitude: -7.6200721020375255, longitude: 109.11965779985853 },
  { name: "KP Sidareja 1", latitude: -7.481438057145171, longitude: 108.79634340518692 },
  { name: "Rumah Ibu", latitude: -7.514161451335894, longitude: 109.06080762883563 } //-7.514161451335894, 109.06080762883563
];

// ==================== UTILITY FUNCTIONS ====================
const calculateDistanceMeters = (lat1, lon1, lat2, lon2) => {
  const R = 6371e3;
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;
  const a = Math.sin(Δφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const formatDateForMySQL = (date = new Date()) => {
  const d = new Date(date);
  const offset = d.getTimezoneOffset() * 60000; // offset in ms
  const localDate = new Date(d.getTime() - offset);
  return localDate.toISOString().split('T')[0];
};

const toMinutes = (timeStr) => {
  const [h = 0, m = 0] = (timeStr ?? '').split(':').map(n => Number(n) || 0);
  return h * 60 + m;
};

const getPeriod = () => {
  const now = new Date();
  let month = now.getMonth();
  const year = now.getFullYear();
  if (now.getDate() >= 21) {
    month += 1;
    if (month > 11) {
      month = 0;
      return `${new Date(year + 1, month).toLocaleString('en-US', { month: 'long' })} ${year + 1}`;
    }
  }
  return `${new Date(year, month).toLocaleString('en-US', { month: 'long' })} ${year}`;
};

const getLocationStayPolicy = (dataKaryawan = {}) => {
  const divisi = String(dataKaryawan.divisi || dataKaryawan.nama_divisi || dataKaryawan.division || '').toLowerCase();
  const kantor = String(dataKaryawan.kantor || dataKaryawan.nama_kantor || dataKaryawan.cabang || dataKaryawan.lokasi || '').toLowerCase();

  if (divisi.includes('pickup')) {
    return { shouldMonitor: false, durationMinutes: 0, label: '' };
  }

  if (divisi.includes('outbound') && kantor.includes('sidareja')) {
    return { shouldMonitor: false, durationMinutes: 0, label: '' };
  }

  if (divisi.includes('delivery')) {
    return { shouldMonitor: true, durationMinutes: 30, label: '30 menit' };
  }

  return { shouldMonitor: true, durationMinutes: 60, label: '1 jam' };
};

const startLocationMonitorAfterAbsenMasuk = (absenData, nearestOffice) => {
  if (!nearestOffice || absenData.status !== 'Absen Masuk') return;

  try {
    const dataKaryawan = JSON.parse(localStorage.getItem('data_karyawan') || '{}');
    const stayPolicy = getLocationStayPolicy(dataKaryawan);
    if (!stayPolicy.shouldMonitor) return;

    const monitors = JSON.parse(localStorage.getItem(LOCAL_LOCATION_MONITOR_KEY) || '[]');
    const now = Date.now();
    const monitor = {
      key: `${dataKaryawan.id || 'unknown'}-${absenData.tanggal}-${absenData.shift_id}-${absenData.waktu}`,
      karyawan_id: dataKaryawan.id || null,
      tanggal: absenData.tanggal,
      waktu: absenData.waktu,
      shift_id: absenData.shift_id,
      office_name: nearestOffice.name,
      office_latitude: nearestOffice.latitude,
      office_longitude: nearestOffice.longitude,
      max_distance: LOCATION_STAY_MAX_DISTANCE_METERS,
      duration_minutes: stayPolicy.durationMinutes,
      duration_label: stayPolicy.label,
      started_at: now,
      expires_at: now + (stayPolicy.durationMinutes * 60 * 1000),
      status: 'pending',
      synced: false,
    };

    const activeMonitors = Array.isArray(monitors)
      ? monitors.filter(item => {
        if (item.key === monitor.key) return false;
        const stillWatching = item.status === 'pending' && item.expires_at > now;
        const waitingSync = item.status === 'left_location' && !item.synced;
        return stillWatching || waitingSync;
      })
      : [];

    localStorage.setItem(LOCAL_LOCATION_MONITOR_KEY, JSON.stringify([...activeMonitors, monitor]));
    window.dispatchEvent(new Event('absenLocationMonitorUpdated'));
  } catch (e) {
    console.error('Gagal membuat monitor lokasi absen:', e);
  }
};

// ==================== CUSTOM HOOK: Jam Real-time ====================
const useClock = () => {
  const [time, setTime] = useState('');
  const [date, setDate] = useState('');

  useEffect(() => {
    const update = () => {
      const now = new Date();

      setTime(
        now.toLocaleTimeString('id-ID', {
          hour12: false,
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        })
      );

      setDate(
        now.toLocaleDateString('id-ID', {
          weekday: 'long',   // Senin, Selasa, ...
          day: 'numeric',
          month: 'long',     // Januari, Februari, ...
          year: 'numeric'
        })
      );
    };

    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, []);

  return { time, date };
};


// ==================== SYNC TO SERVER (DIPERBAIKI) ====================
const syncToDatabase = async (absenData, currentTime, retryCount = 0) => {
  try {
    const dataKaryawan = JSON.parse(localStorage.getItem('data_karyawan') || '{}');
    const karyawanId = dataKaryawan.id ? String(dataKaryawan.id) : null;
    if (!karyawanId) throw new Error('ID karyawan tidak ditemukan');

    const riwayat = JSON.parse(localStorage.getItem(LOCAL_RIWAYAT_KEY) || '[]');
    const absenMasuk = riwayat.find(
      (i) => i.tanggal === absenData.tanggal && i.status === 'Absen Masuk' && i.shift_id === absenData.shift_id
    );

    const payload = {
      karyawan_id: karyawanId,
      absen_id: absenData.absen_id || 18,
      periode: getPeriod(),
      tanggal: formatDateForMySQL(absenData.tanggal),
      jadwal: absenData.jam_masuk || '00:00:00',
      masuk: absenData.status === 'Absen Masuk' ? currentTime : (absenMasuk?.waktu || null),
      pulang: absenData.status === 'Absen Pulang' ? currentTime : null,
      telat: absenData.telat || 0,
      bolos: absenData.bolos || 0,
    };

    const res = await fetch('https://jne-cilacap.com/slip/public/api/absen', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || 'Server error');
    }

    // SUCCESS → tandai synced
    const updated = riwayat.map((item) =>
      item.waktu === absenData.waktu && item.tanggal === absenData.tanggal
        ? { ...item, synced: true }
        : item
    );
    localStorage.setItem(LOCAL_RIWAYAT_KEY, JSON.stringify(updated));

    return { success: true, data: absenData }; // <-- PENTING!

  } catch (err) {
    console.error('Sync gagal:', err);

    // Kalau masih bisa retry → lempar lagi biar caller tahu "belum selesai"
    if (retryCount < SYNC_RETRY_LIMIT) {
      await new Promise(resolve => setTimeout(resolve, SYNC_RETRY_INTERVAL_MS));
      return syncToDatabase(absenData, currentTime, retryCount + 1); // retry
    }

    // Kalau sudah habis retry → permanen gagal
    return { success: false, error: err.message, data: absenData };
  }
};

const mergeLocalLibur = (serverItems = []) => {
  let localItems = [];
  try {
    localItems = JSON.parse(localStorage.getItem(LOCAL_RIWAYAT_KEY) || '[]');
  } catch {
    localItems = [];
  }
  const localLibur = Array.isArray(localItems)
    ? localItems.filter(item => Number(item.absen_id) === 7)
    : [];
  const merged = [...(Array.isArray(serverItems) ? serverItems : [])];
  localLibur.forEach(localItem => {
    const alreadyExists = merged.some(serverItem =>
      Number(serverItem.absen_id) === 7 &&
      String(serverItem.tanggal) === String(localItem.tanggal) &&
      String(serverItem.shift_id || '') === String(localItem.shift_id || '')
    );
    if (!alreadyExists) merged.push(localItem);
  });
  return merged;
};

// ==================== KOMPONEN UTAMA ====================
const Absen = () => {
  const navigate = useNavigate();
  const { time, date } = useClock();

  const [status, setStatus] = useState('Mengecek lokasi...');
  const [distance, setDistance] = useState(null);
  const [nearestOffice, setNearestOffice] = useState(null);
  const [shiftList, setShiftList] = useState([]);
  const [selectedShift, setSelectedShift] = useState(null);
  const [modeAbsen, setModeAbsen] = useState('nonaktif');
  const [showHistory, setShowHistory] = useState(false);
  const [riwayatAbsen, setRiwayatAbsen] = useState([]);
  const [showTodayModal, setShowTodayModal] = useState(false);
  const [todayAbsen, setTodayAbsen] = useState(null);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [selectedReason, setSelectedReason] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [showTutorial, setShowTutorial] = useState(false);

  const openTutorialModal = () => setShowTutorial(true);
  const closeTutorialModal = () => setShowTutorial(false);


  // === BUKA MODAL + LOAD DATA IJIN ===
  const loadIjinMasterData = useCallback(async () => {
    const karyawan = JSON.parse(localStorage.getItem('data_karyawan') || '{}');
    if (!karyawan.id) {
      Swal.fire('Error', 'Data karyawan tidak ditemukan', 'error');
      return false;
    }

    try {
      const [ijinRes, karyawanRes] = await Promise.all([
        fetch('https://jne-cilacap.com/slip/public/api/master/jenis-ijin'),
        fetch('https://jne-cilacap.com/slip/public/api/master/karyawan-pengganti')
      ]);

      let success = true;

      if (ijinRes.ok) {
        const ijinJson = await ijinRes.json();
        if (ijinJson.success && Array.isArray(ijinJson.data)) {
          localStorage.setItem(LOCAL_JENIS_IJIN_KEY, JSON.stringify(ijinJson.data));
        } else success = false;
      } else success = false;

      if (karyawanRes.ok) {
        const karyawanJson = await karyawanRes.json();
        if (karyawanJson.success && Array.isArray(karyawanJson.data)) {
          const filtered = karyawanJson.data.filter(k => k.id !== karyawan.id);
          localStorage.setItem(LOCAL_KARYAWAN_PENGGANTI_KEY, JSON.stringify(filtered));
        } else success = false;
      } else success = false;

      return success;
    } catch (e) {
      console.error('Error load ijin data:', e);
      return false;
    }
  }, []);

  const handleBukaRiwayat = async () => {
    setShowHistory(true); // Buka modal dulu biar UX cepat

    try {
      const karyawan = JSON.parse(localStorage.getItem('data_karyawan') || '{}');
      if (!karyawan?.id) {
        console.warn('Karyawan ID tidak ditemukan');
        return;
      }

      const periode = getPeriod();
      const url = `https://jne-cilacap.com/slip/public/api/riwayat-absen/${karyawan.id}?periode=${encodeURIComponent(periode)}`;

      const res = await fetch(url, {
        method: 'GET',
        headers: {
          'Cache-Control': 'no-cache',
        },
      });

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      }

      const json = await res.json();

      if (json.success && Array.isArray(json.data)) {
        const mergedRiwayat = mergeLocalLibur(json.data);
        // SUKSES: Simpan ke localStorage + update state dengan referensi baru
        localStorage.setItem(LOCAL_ABSEN_SERVER_KEY, JSON.stringify(mergedRiwayat));
        console.log('ini data dari server:', json.data);

        // Paksa re-render meskipun isinya sama
        setRiwayatAbsen([...mergedRiwayat]); // termasuk catatan libur lokal lama

        console.log('Riwayat absen diperbarui dari server:', json.data.length, 'entri');
      } else {
        throw new Error('Response tidak valid atau success=false');
      }
    } catch (error) {
      console.error('Gagal mengambil riwayat absen:', error.message || error);

      // GUNAKAN DATA CACHE (OFFLINE MODE) — DENGAN PENGECEKAN AMAN
      const dataLama = localStorage.getItem(LOCAL_ABSEN_SERVER_KEY);

      if (dataLama) {
        try {
          const parsed = JSON.parse(dataLama);
          if (Array.isArray(parsed)) {
            // Paksa re-render dengan referensi baru
            setRiwayatAbsen([...parsed]);
            console.log('Menggunakan data riwayat dari cache (offline mode)');
          } else {
            setRiwayatAbsen([]); // kosongin kalau format salah
          }
        } catch (e) {
          console.error('Cache riwayat absen rusak:', e);
          setRiwayatAbsen([]);
        }
      } else {
        // Belum pernah ada data sama sekali
        setRiwayatAbsen([]);
        console.log('Tidak ada cache riwayat absen');
      }

      // Optional: beri tahu user
      // Swal.fire('Info', 'Menampilkan data terakhir (mode offline)', 'info');
    }
  };

  const openPhotoModal = useCallback(async (reason) => {
    if (reason === 'ijin') {
      const hasIjin = localStorage.getItem(LOCAL_JENIS_IJIN_KEY);
      const hasPengganti = localStorage.getItem(LOCAL_KARYAWAN_PENGGANTI_KEY);

      if (!hasIjin || !hasPengganti) {
        Swal.fire({ title: 'Memuat data...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
        const success = await loadIjinMasterData();
        Swal.close();
        if (!success) {
          Swal.fire('Gagal', 'Tidak dapat memuat data ijin.', 'error');
          return;
        }
      }
    }
    setSelectedReason(reason);
    setShowPhotoModal(true);
  }, [loadIjinMasterData]);

  const closePhotoModal = () => {
    setShowPhotoModal(false);
    setSelectedReason(null);
    setPhoto(null);
    setIsSubmitting(false);
  };

  const allItems = riwayatAbsen;

  // ==================== LOAD DATA AWAL ====================
  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('data_shift') || '[]');
      const unique = [...new Map(stored.map((s) => [s.id, s])).values()];
      setShiftList(unique);

      const selected = JSON.parse(localStorage.getItem('shift_terpilih') || 'null');
      if (selected) setSelectedShift(selected);
    } catch (e) {
      Swal.fire('Error', 'Gagal memuat shift', 'error');
    }

    const fetchRiwayat = async () => {
      try {
        const karyawan = JSON.parse(localStorage.getItem('data_karyawan') || '{}');
        if (!karyawan.id) return;

        const res = await fetch(
          `https://jne-cilacap.com/slip/public/api/riwayat-absen/${karyawan.id}?periode=${encodeURIComponent(getPeriod())}`
        );
        const json = await res.json();
        if (json.success) {
          const mergedRiwayat = mergeLocalLibur(json.data);
          localStorage.setItem(LOCAL_ABSEN_SERVER_KEY, JSON.stringify(mergedRiwayat));
          setRiwayatAbsen(mergedRiwayat);

        }
      } catch (e) {
        console.error('Gagal ambil riwayat:', e);
      }
    };
    fetchRiwayat();

    const loadTodayAbsen = () => {
      const stored = localStorage.getItem(LOCAL_RIWAYAT_KEY);
      if (!stored) return;
      try {
        const riwayat = JSON.parse(stored);
        const today = formatDateForMySQL(new Date());
        const todayData = riwayat.filter(a => a.tanggal === today).pop();
        setTodayAbsen(todayData || null);
      } catch (e) {
        console.error('Gagal parse riwayat:', e);
      }
    };
    loadTodayAbsen();
  }, []);

  // ==================== GEOLOCATION - SUDAH DIPERBAIKI 100% ====================
  useEffect(() => {
    if (!navigator.geolocation) {
      setStatus('Browser tidak mendukung GPS');
      setDistance(null);
      setNearestOffice(null);
      return;
    }

    const allowed = JSON.parse(localStorage.getItem('data_lokasi_absen') || '[]');
    if (!allowed.length) {
      setStatus('Tidak ada lokasi izin, silahkan klik Update di halaman utama');
      setDistance(null);
      setNearestOffice(null);
      return;
    }

    const validOffices = OFFICE_LOCATIONS.filter((office) =>
      allowed.some((izin) => office.name.toLowerCase().includes(izin.nama.toLowerCase()))
    );

    if (!validOffices.length) {
      setStatus('Lokasi kantor tidak diizinkan');
      setDistance(null);
      setNearestOffice(null);
      return;
    }

    let watchId = null;
    let pernahBerhasil = false;

    const sukses = (pos) => {
      const { latitude, longitude } = pos.coords;
      const distances = validOffices.map((loc) => ({
        ...loc,
        distance: calculateDistanceMeters(latitude, longitude, loc.latitude, loc.longitude),
      }));
      const nearest = distances.reduce((a, b) => (a.distance < b.distance ? a : b));

      pernahBerhasil = true;
      setDistance(nearest.distance);
      setNearestOffice(nearest);
      setStatus(`${(nearest.distance / 1000).toFixed(1)} km dari ${nearest.name}`);
      localStorage.setItem('lokasi_terdeteksi', JSON.stringify(nearest));
    };

    const gagal = () => {
      if (!pernahBerhasil) {
        setStatus('Gagal deteksi GPS');
        setDistance(null);
        setNearestOffice(null);

        Swal.fire({
          icon: 'warning',
          title: 'GPS Tidak Bisa Mendeteksi Lokasi',
          text: 'Silakan pilih alasan absen GPS Error',
          timer: 4000,
          showConfirmButton: false,
        });
      }
    };

    // Ambil posisi pertama kali
    navigator.geolocation.getCurrentPosition(sukses, gagal, {
      enableHighAccuracy: true,
      timeout: 15000,
      maximumAge: 60000,
    });

    // Terus pantau selama halaman terbuka
    watchId = navigator.geolocation.watchPosition(sukses, gagal, {
      enableHighAccuracy: true,
      timeout: 15000,
      maximumAge: 30000,
    });

    // Cleanup saat keluar halaman
    return () => {
      if (watchId !== null) {
        navigator.geolocation.clearWatch(watchId);
      }
    };
  }, []); // ← dependency kosong, sama seperti kode lama kamu

  useEffect(() => {
    if (!selectedShift) {
      setModeAbsen("nonaktif");
      return;
    }

    const now = new Date();
    const nowMins = now.getHours() * 60 + now.getMinutes();

    const masuk = toMinutes(selectedShift.jam_masuk);
    const pulangRaw = toMinutes(selectedShift.jam_pulang);

    const isShiftMalam = pulangRaw <= masuk;
    const pulang = isShiftMalam ? pulangRaw + 1440 : pulangRaw;

    const BEFORE = 270;       // 4.5 jam sebelum
    const AFTER_MASUK = 270;  // 4.5 jam setelah
    const AFTER_PULANG = 720; // 12 jam setelah pulang (cukup untuk lembur sampai 07:00)

    // ----- NORMALISASI UNTUK PULANG SAJA -----
    let nowFix = nowMins;
    // Jika sekarang dini hari (sampai jam 7 pagi)
    // ATAU shift malam dan sekarang sebelum jam masuk
    // → tambah 1440 agar jam dini hari tidak dianggap "hari baru"
    if (nowMins < 420 || (isShiftMalam && nowMins < masuk)) {
      nowFix += 1440;
    }

    // ====== WINDOW PULANG (JANGAN UBAH) ======
    let isPulangWindow =
      nowFix >= pulang - BEFORE &&
      nowFix <= pulang + AFTER_PULANG;

    // Khusus untuk shift 19:00 - 07:00:
    const isShift19 = selectedShift.jam_masuk?.substring(0, 5) === '19:00' && selectedShift.jam_pulang?.substring(0, 5) === '07:00';
    if (isShift19) {
      if (nowMins >= 1020 && nowMins <= 1410) {
        isPulangWindow = false;
      }
    }

    // Khusus untuk shift 07:00 - 19:00:
    const isShift07 = selectedShift.jam_masuk?.substring(0, 5) === '07:00' && selectedShift.jam_pulang?.substring(0, 5) === '19:00';
    if (isShift07) {
      if (nowMins >= 300 && nowMins <= 690) {
        isPulangWindow = false;
      }
    }

    if (isPulangWindow) {
      setModeAbsen("pulang");
      return;
    }

    // ====== WINDOW MASUK (PERBAIKAN DI SINI) ======
    const masukStart = masuk - BEFORE;      // 4.5 jam sebelum masuk
    const masukEnd = masuk + AFTER_MASUK; // 4.5 jam sesudah masuk

    const isMasukWindow =
      (nowMins >= masukStart && nowMins <= masukEnd) ||
      (masukStart < 0 && (nowMins <= masukEnd || nowMins >= masukStart + 1440));

    if (isMasukWindow) {
      setModeAbsen("masuk");
      return;
    }

    setModeAbsen("nonaktif");

  }, [selectedShift, time]);

  // ==================== SYNC UNSYNCED ====================
  const syncUnsynced = useCallback(() => {
    const riwayat = JSON.parse(localStorage.getItem(LOCAL_RIWAYAT_KEY) || '[]');
    riwayat.filter(i => !i.synced).forEach(item => syncToDatabase(item, item.waktu));
  }, []);

  useEffect(() => {
    syncUnsynced();
    const id = setInterval(syncUnsynced, 30000);
    return () => clearInterval(id);
  }, [syncUnsynced]);

  // ==================== HANDLE ABSEN ====================
  const handleAbsen = useCallback(() => {
    if (!selectedShift) return Swal.fire('Pilih shift dulu!', '', 'warning');
    if (modeAbsen === 'nonaktif') return Swal.fire('Bukan jam absen', '', 'info');
    if (!nearestOffice || distance > MAX_DISTANCE_METERS)
      return Swal.fire(`Anda ${distance.toFixed(0)}m dari ${nearestOffice?.name}`, 'Dekati kantor!', 'warning');

    const now = new Date();
    const currentTime = time;

    // ===== INI YANG BARU: Tanggal dasar tetap hari ini =====
    let tanggalAbsen = new Date();
    tanggalAbsen.setHours(0, 0, 0, 0); // normalize
    let isCrossoverDay = false;

    if (modeAbsen === 'pulang' && selectedShift) {
      const jamMasukMins = toMinutes(selectedShift.jam_masuk);
      const sekarangMins = now.getHours() * 60 + now.getMinutes();

      // Mundurkan ke kemarin jika:
      // 1. Sekarang sebelum jam masuk (crossover tengah malam, shift malam murni)
      // 2. Atau dini hari (< 07:00) dan shift siang/sore (mulai >= 13:00)
      //    → shift 13:00, 15:00, 15:30 yang lembur sampai pagi
      const isTengahMalam = sekarangMins < jamMasukMins;
      const isLemburPagi = sekarangMins < 420 && jamMasukMins >= 780; // 780 = 13*60

      if (isTengahMalam || isLemburPagi) {
        tanggalAbsen.setDate(tanggalAbsen.getDate() - 1);
        isCrossoverDay = true;
        console.log('Absen pulang lembur → tanggal dikembalikan ke:', tanggalAbsen.toLocaleDateString('id-ID'));
      }
    }

    const currentDate = formatDateForMySQL(tanggalAbsen); // ← tetap pakai variabel ini seperti semula
    console.log('Tanggal absen yang digunakan:', currentDate);

    const riwayat = JSON.parse(localStorage.getItem(LOCAL_RIWAYAT_KEY) || '[]');
    const statusAbsen = modeAbsen === 'masuk' ? 'Absen Masuk' : 'Absen Pulang';

    // Fungsi hapus item terakhir
    const hapusRiwayatTerakhir = () => {
      try {
        const raw = localStorage.getItem('riwayat_absen');
        if (!raw) return;

        const riwayat = JSON.parse(raw);

        if (!Array.isArray(riwayat) || riwayat.length === 0) return;

        riwayat.pop(); // hapus item terakhir

        localStorage.setItem('riwayat_absen', JSON.stringify(riwayat));

        console.log("Riwayat terakhir sudah dihapus. Sisa data:", riwayat.length);
      } catch (e) {
        console.error("Gagal hapus item terakhir:", e);
      }
    };

    // Terapkan ke logika shift
    if (riwayat.some(i => i.tanggal === currentDate && i.shift_id !== selectedShift.id)) {

      // hapus item terakhir terlebih dahulu
      hapusRiwayatTerakhir();

      // lalu tampilkan peringatan
      return Swal.fire(
        'Shift berbeda hari ini!, silahkan klik Ok.',
        '',
        'warning'
      );
    }

    if (riwayat.some((i) => i.tanggal === currentDate && i.status === statusAbsen && i.shift_id === selectedShift.id))
      return Swal.fire(`Sudah ${statusAbsen} hari ini`, '', 'warning');

    const rawNowMins = now.getHours() * 60 + now.getMinutes();
    const masukMins = toMinutes(selectedShift.jam_masuk);
    const pulangRawMins = toMinutes(selectedShift.jam_pulang);

    // Deteksi shift malam (jam pulang <= jam masuk, berarti melewati tengah malam)
    const isShiftMalamCalc = pulangRawMins <= masukMins;
    // Normalisasi jam pulang agar di "atas" jam masuk
    const pulangMins = isShiftMalamCalc ? pulangRawMins + 1440 : pulangRawMins;
    // Normalisasi nowMins: jika shift malam ATAU crossover day & absen pulang & sekarang dini hari (< jam masuk), tambah 1440
    const nowMins = (modeAbsen === 'pulang' && (isShiftMalamCalc || isCrossoverDay) && rawNowMins < masukMins) ? rawNowMins + 1440 : rawNowMins;

    let telat = 0, bolos = 0, uang_makan = 0;

    if (modeAbsen === 'masuk' && nowMins - masukMins > 11) telat = nowMins - masukMins;
    if (modeAbsen === 'pulang' && pulangMins - nowMins > 10) bolos = pulangMins - nowMins;

    if (modeAbsen === 'pulang') {
      const masukHariIni = riwayat.find((i) => i.tanggal === currentDate && i.status === 'Absen Masuk');
      if (masukHariIni && telat < 11 && bolos < 10) uang_makan = 12500;
    }

    const jamMasuk = selectedShift.jam_masuk ? selectedShift.jam_masuk.substring(0, 5) : '00:00';
    const jamPulang = selectedShift.jam_pulang ? selectedShift.jam_pulang.substring(0, 5) : '00:00';
    const jadwalFormatted = `${jamMasuk} - ${jamPulang}`;

    const absenData = {
      waktu: currentTime,
      tanggal: currentDate,
      status: statusAbsen,
      shift_id: selectedShift.id,
      shift_nama: selectedShift.shift,
      jam_masuk: selectedShift.jam_masuk,
      jam_pulang: selectedShift.jam_pulang,
      jadwal: jadwalFormatted,
      telat,
      bolos,
      uang_makan,
      synced: false,
    };

    console.log('Data absen yang dicatat:', absenData);

    riwayat.push(absenData);
    localStorage.setItem(LOCAL_RIWAYAT_KEY, JSON.stringify(riwayat));
    startLocationMonitorAfterAbsenMasuk(absenData, nearestOffice);
    setTodayAbsen(absenData);
    syncToDatabase(absenData, currentTime);
    navigate('/selesai', { state: { absenData } });
  }, [selectedShift, modeAbsen, nearestOffice, distance, time, navigate]);

  // ==================== STYLING TOMBOL ====================
  const getButtonStyles = useCallback(() => {
    if (modeAbsen === 'masuk')
      return { bg: 'linear-gradient(145deg, #20cd73, #18b865)', border: '#18b865' };
    if (modeAbsen === 'pulang')
      return { bg: 'linear-gradient(145deg, #ff0022, #d8001c)', border: '#d8001c' };
    return { bg: 'linear-gradient(145deg, #dcdcdc, #bcbcbc)', border: '#a0a0a0' };
  }, [modeAbsen]);

  const btn = getButtonStyles();
  const tooFar = distance !== null && distance > MAX_DISTANCE_METERS;
  const disabled = modeAbsen === 'nonaktif' || tooFar;
  const label = tooFar ? 'Tidak di kantor' : modeAbsen === 'masuk' ? 'Absen Masuk' : modeAbsen === 'pulang' ? 'Absen Pulang' : 'Diluar Jam Shift';

  const lightenColor = (hex, percent) => {
    const num = parseInt(hex.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) + amt;
    const G = ((num >> 8) & 0x00ff) + amt;
    const B = (num & 0x0000ff) + amt;
    return '#' + (0x1000000 + (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
      (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
      (B < 255 ? (B < 1 ? 0 : B) : 255)).toString(16).slice(1);
  };

  const getIconForReason = useCallback((reason) => {
    const r = reason.toLowerCase();
    switch (r) {
      case 'libur': return <Calendar size={18} />;
      case 'dinas': return <Briefcase size={18} />;
      case 'ijin': return <FileText size={18} />;
      case 'lapangan': return <MapPin size={18} />;
      case 'gps error': return <AlertCircle size={18} />;
      default: return <HelpCircle size={18} />;
    }
  }, []);

  const renderTombolLuar = useMemo(() => {
    const gpsGagal = distance === null || nearestOffice === null;
    const terlaluJauh = distance !== null && distance > MAX_DISTANCE_METERS;

    // Muncul kalau GPS gagal ATAU memang jauh dari kantor
    if (!gpsGagal && !terlaluJauh) return null;

    return ['libur', 'dinas', 'ijin', 'lapangan', 'gps error'].map((item, index) => {
      const angle = (index * 72 - 90) * (Math.PI / 180);
      const radius = 110;
      const x = Math.cos(angle) * radius;
      const y = Math.sin(angle) * radius;

      const colors = ['#20cd73', '#ffb800', '#ff6b6b', '#4ecdc4', '#9c27b0'];
      const bg = `linear-gradient(145deg, ${colors[index]}, ${lightenColor(colors[index], -18)})`;

      return (
        <motion.button
          key={item}
          onClick={() => openPhotoModal(item)}
          style={{
            position: 'absolute',
            top: `calc(40% + ${y}px)`,
            left: `calc(50% + ${x}px)`,
            transform: 'translate(-50%, -50%)',
            width: 76,
            height: 76,
            borderRadius: '50%',
            background: bg,
            border: `4px solid ${colors[index]}`,
            color: '#fff',
            fontSize: 10,
            fontWeight: 'bold',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '5px 5px 12px rgba(0,0,0,0.25)',
            textTransform: 'capitalize',
            marginTop: 8,
            zIndex: 5,
            padding: 6,
          }}
        >
          {getIconForReason(item)}
          <span style={{ marginTop: 4, fontSize: 11, lineHeight: 1, textAlign: 'center' }}>
            {item === 'gps error' ? 'GPS\nError' : item}
          </span>
        </motion.button>
      );
    });
  }, [distance, nearestOffice, openPhotoModal, getIconForReason]);

  const renderShiftList = useMemo(() => {
    if (shiftList.length === 0) {
      return (
        <p style={{ fontSize: 13, color: "#555", fontStyle: "italic", textAlign: "center" }}>
          Tidak ada data shift silahkan klik Update di halaman utama.
        </p>
      );
    }

    return shiftList.map((item) => {
      const active = selectedShift?.id === item.id;

      return (
        <div
          key={item.id}
          onClick={() => {
            setSelectedShift(item);
            localStorage.setItem("shift_terpilih", JSON.stringify(item));
          }}
          style={{
            minWidth: 159,
            padding: "12px 16px",
            borderRadius: 24,

            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            cursor: "pointer",

            background: active
              ? "linear-gradient(135deg, #0099ff 0%, #0077cc 100%)"
              : "#ffffff",

            border: active
              ? "1px solid #0089e0"
              : "1px solid #e3eef7",

            boxShadow: active
              ? "0 4px 12px rgba(0, 123, 209, 0.25)"
              : "0 2px 6px rgba(0,0,0,0.05)",

            color: active ? "#fff" : "#222",
            transition: "all .25s ease",
          }}
        >

          {/* ICON KIRI */}
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 12,
              background: active ? "rgba(255,255,255,0.35)" : "#eaf5ff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 16,
            }}
          >
            🕒
          </div>


          {/* TEKS SHIFT */}
          <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.1 }}>
            <span style={{ fontSize: 13, fontWeight: 700 }}>
              {item.shift_name || item.shift?.split(" ")[0] || "Shift"}
            </span>

            <span style={{ fontSize: 12, opacity: active ? 0.9 : 0.7 }}>
              {item.shift_time || item.shift?.split(" ").slice(1).join(" ") || ""}
            </span>
          </div>


          {/* ICON PANAH */}
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 12,
              background: active ? "rgba(255,255,255,0.35)" : "#eaf5ff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 14,
            }}
          >
            ↗
          </div>
        </div>
      );
    });
  }, [shiftList, selectedShift]);


  const groupedRiwayat = useMemo(() => {
    if (!riwayatAbsen.length) return {};

    const groups = {};

    riwayatAbsen.forEach(item => {
      // Ambil tanggal yang valid
      let tanggal = item.tanggal || '';

      // Pastikan formatnya benar YYYY-MM-DD
      if (!/^\d{4}-\d{2}-\d{2}$/.test(tanggal)) {
        if (item.tgl_ijin && /^\d{4}-\d{2}-\d{2}$/.test(item.tgl_ijin)) {
          tanggal = item.tgl_ijin;
        } else {
          const match = tanggal.match(/^(\d{2})-(\d{2})-(\d{4})$/);
          if (match) {
            tanggal = `${match[3]}-${match[2]}-${match[1]}`;
          } else {
            tanggal = '0000-00-00';
          }
        }
      }

      if (!groups[tanggal]) groups[tanggal] = [];
      groups[tanggal].push(item);
    });

    // Sorting descending: terbaru di atas → cukup pakai localeCompare (string YYYY-MM-DD sudah aman!)
    return Object.keys(groups)
      .sort((a, b) => b.localeCompare(a))  // <-- ini paling stabil untuk format YYYY-MM-DD
      .reduce((acc, tanggal) => {
        acc[tanggal] = groups[tanggal];
        return acc;
      }, {});
  }, [riwayatAbsen]);


  const hasData = Object.keys(groupedRiwayat).length > 0;
  const shiftHint = shiftList.length > 1
    ? " ( Geser ke samping ➡️ )"
    : "";

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(180deg, #F5F9FF 0%, #E8F1FF 40%, #FFFFFF 100%)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        position: "relative",
        padding: "20px 0",
        fontFamily: "Poppins, sans-serif",
        overflow: "hidden",
      }}
    >

      {/* TOMBOL BACK */}
      <motion.div
        onClick={() => navigate('/')}
        initial={{ opacity: 0, scale: 0.7 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4 }}
        style={{
          position: 'absolute',
          top: 15,
          left: 15,
          height: 40,
          width: 40,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: '#fff',
          borderRadius: 20,
          padding: 8,
          cursor: 'pointer',
          boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
          marginTop: -3,
          zIndex: 10,
        }}
      >
        <ArrowLeft size={22} color="#333" />
      </motion.div>

      {/* JAM & R */}
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} style={{ textAlign: 'center', marginTop: 20 }}>
        <h1 style={{ fontSize: 52, color: '#000', margin: 0 }}>{time}</h1>
        <p style={{ fontSize: 16, color: '#757575', margin: 0 }}>{date}</p>

      </motion.div>

      {/* STATUS JARAK */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6, duration: 0.8 }} style={{ marginTop: 5, textAlign: 'center' }}>
        <p style={{ color: '#444', fontSize: 15, marginBottom: 5 }}>{status}</p>
        {distance && (
          <small style={{ color: distance > 50 ? 'red' : '#777' }}>
            Jarak ke kantor: {distance.toFixed(0)} meter
          </small>
        )}
      </motion.div>

      {/* TOMBOL ABSEN UTAMA */}
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', stiffness: 120, damping: 10, delay: 0.9 }}
        style={{ position: 'relative', width: tooFar ? 340 : 300, height: tooFar ? 240 : 200, marginTop: 60 }}
      >
        <motion.button
          onClick={() => {
            if (disabled) {
              openTutorialModal(); // ⬅️ tetap bisa jalan
            } else {
              handleAbsen();
            }
          }}
          style={{
            position: 'absolute',
            top: '44%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: tooFar ? 110 : 160,
            height: tooFar ? 110 : 160,
            borderRadius: '50%',
            background: tooFar ? 'linear-gradient(145deg, #dcdcdc, #bcbcbc)' : btn.bg,
            border: `5px solid ${tooFar ? '#a0a0a0' : btn.border}`,
            color: tooFar ? '#666' : '#fff',
            fontSize: tooFar ? 13 : 18,
            fontWeight: 'bold',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.6 : 1,
            zIndex: 10,
            boxShadow: tooFar
              ? '0 2px 6px rgba(0,0,0,0.15)'
              : '8px 8px 24px rgba(0,0,0,0.3), -8px -8px 24px rgba(255,255,255,0.1)',
            transition: 'all 0.3s ease',
            pointerEvents: tooFar ? 'auto' : 'auto', // biarkan bisa klik
          }}
        >
          {tooFar ? (
            <>
              <HelpCircle size={38} color="#999" />
              <span style={{ fontSize: 12, marginTop: 6 }}>Tidak di Kantor</span>
            </>
          ) : (
            <>
              <Fingerprint size={58} color="#fff" strokeWidth={2.3} style={{ marginBottom: 12, filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.4))' }} />
              <span style={{ fontSize: 15 }}>{label}</span>
            </>
          )}
        </motion.button>

        {showTutorial && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              background: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 999,
              backdropFilter: 'blur(3px)',
            }}
            onClick={closeTutorialModal} // klik luar tutup
          >
            <motion.div
              initial={{ scale: 0.7, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.7, opacity: 0 }}
              style={{
                background: '#fff',
                borderRadius: 14,
                width: '85%',
                maxWidth: 380,
                maxHeight: '80vh',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
              }}
              onClick={(e) => e.stopPropagation()}
            >

              {/* HEADER */}
              <div
                style={{
                  padding: '18px 20px',
                  borderBottom: '1px solid #e5e5e5',
                  textAlign: 'center',
                }}
              >
                <h3 style={{ fontSize: 18, margin: 0, fontWeight: 700 }}>
                  Panduan Absensi Tidak di Kantor
                </h3>
              </div>

              {/* BODY / SCROLLABLE CONTENT */}
              <div
                style={{
                  padding: '18px 20px',
                  overflowY: 'auto',
                  flexGrow: 1,
                }}
              >
                {/* 1. Libur */}
                <h4 style={{ fontSize: 16, color: '#0a0101ff', marginBottom: 6 }}>
                  1. Libur
                </h4>
                <ul style={{ paddingLeft: 20, marginBottom: 16 }}>
                  <li>Digunakan untuk menandai hari libur resmi atau sesuai jadwal kerja.</li>
                  <li>Membantu membedakan antara status libur dan tidak hadir tanpa keterangan.</li>
                  <li>Cukup dilakukan satu kali dalam satu hari.</li>
                </ul>

                {/* 2. Dinas */}
                <h4 style={{ fontSize: 16, color: '#0a0101ff', marginBottom: 6 }}>
                  2. Dinas
                </h4>
                <ul style={{ paddingLeft: 20, marginBottom: 16 }}>
                  <li>Digunakan saat Anda melakukan perjalanan Dinas.</li>
                  <li>Memberikan informasi kepada HC mengenai aktivitas dinas Anda.</li>
                  <li>Cukup dilakukan satu kali dalam satu hari.</li>
                </ul>

                {/* 3. Izin */}
                <h4 style={{ fontSize: 16, color: '#0a0101ff', marginBottom: 6 }}>
                  3. Izin
                </h4>
                <ul style={{ paddingLeft: 20, marginBottom: 16 }}>
                  <li>Digunakan ketika Anda perlu izin mendadak pada hari tersebut.</li>
                  <li>Memberitahukan kepada HC dan karyawan pengganti mengenai ketidakhadiran Anda.</li>
                  <li>Cukup dilakukan satu kali dalam satu hari.</li>
                </ul>

                {/* 4. Lapangan */}
                <h4 style={{ fontSize: 16, color: '#0a0101ff', marginBottom: 6 }}>
                  4. Lapangan
                </h4>
                <ul style={{ paddingLeft: 20, marginBottom: 16 }}>
                  <li>
                    Digunakan saat Anda langsung bertugas ke lapangan dan tidak dapat ke kantor untuk melakukan absen.
                  </li>
                  <li>Mendokumentasikan absen saat berada di lokasi pekerjaan.</li>
                  <li>Khusus digunakan untuk Absen Pulang.</li>
                  <li>Untuk Absen Masuk perlu konfirmasi Atasan terlebih dahulu.</li>
                </ul>

                {/* 5. GPS Error */}
                <h4 style={{ fontSize: 16, color: '#0a0101ff', marginBottom: 6 }}>
                  5. GPS Error
                </h4>
                <ul style={{ paddingLeft: 20, marginBottom: 16 }}>
                  <li>Digunakan saat Anda sudah berada di kantor namun GPS tidak dapat mendeteksi lokasi dengan tepat.</li>
                  <li>Bersifat kondisional dan hanya jika terjadi kendala teknis lokasi.</li>
                </ul>
              </div>

              {/* FOOTER */}
              <div
                style={{
                  padding: '14px 20px',
                  borderTop: '1px solid #e5e5e5',
                  textAlign: 'center',
                }}
              >
                <button
                  onClick={closeTutorialModal}
                  style={{
                    height: '48px',                 // ⬅️ disamakan dengan tinggi tombol shift
                    padding: '0 22px',              // biar teks tetap nyaman
                    fontSize: 15,
                    width: '100%',
                    padding: '10px 0',
                    background: '#216fdc',
                    color: '#fff',
                    fontWeight: '600',
                    border: 'none',
                    borderRadius: 8,
                    cursor: 'pointer',
                  }}
                >
                  Mengerti
                </button>
              </div>

            </motion.div>
          </motion.div>
        )}

        {/* TOMBOL LUAR */}
        {renderTombolLuar}

        {/* MODAL FOTO LUAR */}
        <AbsenLuarPhotoModal
          show={showPhotoModal}
          onClose={closePhotoModal}
          reason={selectedReason}
          photo={photo}
          onPhotoChange={setPhoto}
          isSubmitting={isSubmitting}
          setIsSubmitting={setIsSubmitting}
        />
      </motion.div>
      <p style={{ fontSize: 13, color: '#555', marginTop: 8, textAlign: 'center', padding: '0 20px' }}>
        {tooFar ? 'Pelaporan Dinas dan Pengajuan Ijin sudah bisa digunakan' : ''}
      </p>
      {/* JAM MASUK & PULANG SHIFT TERPILIH */}
      {!tooFar && (
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} style={{ textAlign: 'center', marginTop: 5 }}>
          {selectedShift && (
            <p style={{ fontSize: 14, color: '#007bff', marginTop: 10 }}>
              Masuk: {selectedShift.jam_masuk} | Pulang: {selectedShift.jam_pulang}
            </p>
          )}
        </motion.div>
      )}

      {/* PILIH SHIFT & TOMBOL */}
      <div style={{
        backgroundColor: '#ffffff',
        padding: 20,
        borderRadius: 20,
        marginTop: 10,
        boxShadow: '0 4px 15px rgba(0, 0, 0, 0.08)',
        border: '1px solid #e8edf5',
        width: 'calc(100% - 32px)',
        maxWidth: 450,
        boxSizing: 'border-box',
      }}>
        {!tooFar && (
          <>
            <h3 style={{ margin: '0 0 16px 0', fontSize: 16, fontWeight: 'bold', color: '#003087', textAlign: 'center' }}>
              Pilih Shift{shiftHint}
            </h3>
            <div style={{
              backgroundColor: '#f6fafd', borderRadius: 12, padding: 12,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              border: '1px solid #d6ecf3', scrollBehavior: 'smooth',
              scrollbarWidth: 'thin',
              overflowX: 'auto', gap: 12
            }}>
              {renderShiftList}
            </div>

          </>
        )}

        <div style={{ display: 'grid', marginTop: 20 }}>
          <button
            onClick={handleBukaRiwayat}
            style={{
              height: '48px',                 // ⬅️ disamakan dengan tinggi tombol shift
              padding: '0 22px',              // biar teks tetap nyaman
              fontSize: 15,
              backgroundColor: '#003087',
              color: '#fff',
              border: 'none',
              borderRadius: 12,               // samain radius style shift yg tidak terlalu bulat
              cursor: 'pointer',
              boxShadow: '0 3px 8px rgba(0,0,0,0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            Riwayat Absen
          </button>

          {/* {todayAbsen && (
            <button
              onClick={() => setShowTodayModal(true)}
              style={{
                padding: '10px 18px',
                fontSize: 14,
                backgroundColor: '#28a745',
                color: '#fff',
                border: 'none',
                borderRadius: 10,
                cursor: 'pointer',
                boxShadow: '0 3px 8px rgba(0,0,0,0.15)',
              }}
            >
              Absen Hari Ini
            </button>
          )} */}
        </div>
      </div>

      {/* MODAL RIWAYAT */}
      {showHistory && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setShowHistory(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0, 0, 0, 0.55)',
            display: 'flex', justifyContent: 'center', alignItems: 'center',
            zIndex: 1000, backdropFilter: 'blur(6px)', padding: '16px',
          }}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%', maxWidth: 440, background: '#ffffff',
              borderRadius: 24, boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
              overflow: 'hidden', fontFamily: "'Poppins', sans-serif",
              maxHeight: '90vh', display: 'flex', flexDirection: 'column',
            }}
          >
            <div
              style={{
                position: 'relative',
                background: 'linear-gradient(135deg, #003087, #0056b3)',
                color: '#fff',
                padding: '18px 24px',
                textAlign: 'center',
                fontSize: 18,
                fontWeight: '700',
                borderRadius: '8px 8px 0 0',
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
              }}
            >
              Riwayat Absen

              {/* Tombol Lihat Data Local (kiri atas) */}
              <button
                onClick={() => {
                  const data = localStorage.getItem('riwayat_absen');

                  if (!data) {
                    Swal.fire({
                      icon: 'info',
                      title: 'Tidak Ada Data',
                      text: '❌ Data Absen tidak ditemukan di penyimpanan lokal anda.',
                      confirmButtonText: 'OK',
                      customClass: { confirmButton: 'btn btn-primary' },
                    });
                    return;
                  }

                  try {
                    const parsed = JSON.parse(data);
                    const sortedData = Array.isArray(parsed)
                      ? [...parsed].sort((a, b) => {
                        const dateA = new Date(`${a.tanggal}T${a.waktu || '00:00:00'}`);
                        const dateB = new Date(`${b.tanggal}T${b.waktu || '00:00:00'}`);
                        return dateB - dateA;
                      })
                      : [];

                    // Grouping data berdasarkan tanggal
                    const groups = sortedData.reduce((acc, item) => {
                      if (!acc[item.tanggal]) acc[item.tanggal] = [];
                      acc[item.tanggal].push(item);
                      return acc;
                    }, {});

                    Swal.fire({
                      title: 'Log Absen Lokal',
                      html: `
                        <div style="max-height: 450px; overflow-y: auto; text-align: left; padding: 5px; font-family: 'Poppins', sans-serif;">
                          ${Object.keys(groups).length === 0 ? '<p style="text-align:center; color:#999; padding: 20px;">Belum ada log aktivitas</p>' :
                          Object.keys(groups).sort((a, b) => new Date(b) - new Date(a)).map(tanggal => `
                              <div style="margin-bottom: 24px;">
                                <div style="
                                  font-size: 13px; 
                                  font-weight: 800; 
                                  color: #003087; 
                                  padding: 4px 12px; 
                                  background: #eef2ff; 
                                  border-radius: 8px;
                                  display: inline-block;
                                  margin-bottom: 12px;
                                ">
                                  ${new Date(tanggal).toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                                </div>
                                
                                <div style="padding-left: 10px; border-left: 2px dashed #e2e8f0; margin-left: 8px;">
                                  ${groups[tanggal].map(item => `
                                    <div style="
                                      position: relative;
                                      padding: 8px 12px;
                                      background: #fff;
                                      border: 1px solid #f1f5f9;
                                      border-radius: 12px;
                                      margin-bottom: 8px;
                                      display: flex;
                                      justify-content: space-between;
                                      align-items: center;
                                      box-shadow: 0 2px 4px rgba(0,0,0,0.02);
                                    ">
                                      <div style="
                                        position: absolute;
                                        left: -17px;
                                        width: 12px;
                                        height: 12px;
                                        border-radius: 50%;
                                        background: ${item.synced ? '#16a34a' : '#ef4444'};
                                        border: 2px solid #fff;
                                        box-shadow: 0 0 0 2px #f8fafc;
                                      "></div>
                                      
                                      <div>
                                        <div style="font-size: 13px; font-weight: 700; color: #1e293b;">
                                          ${item.status || 'Absen'}
                                        </div>
                                        <div style="font-size: 11px; color: #64748b; margin-top: 1px;">
                                           Jam ${item.waktu || '-'}
                                        </div>
                                      <div style="font-size: 12px; color: #555; margin-top: 2px;">
                                        ID: ${item.absen_id ?? (item.status === 'Libur' ? 7 : 18)}
                                      </div>
                                      <div style="font-size: 12px; color: #555; margin-top: 2px;">
                                        Shift: ${item.shift_nama ?? '-'}
                                      </div>
                                      </div>
                                      
                                      <div style="
                                        font-size: 9px; 
                                        padding: 3px 8px; 
                                        border-radius: 20px; 
                                        background: ${item.synced ? '#dcfce7' : '#fee2e2'}; 
                                        color: ${item.synced ? '#16a34a' : '#ef4444'};
                                        font-weight: 800;
                                        text-transform: uppercase;
                                      ">
                                        ${item.synced ? 'Terkirim' : 'Pending'}
                                      </div>
                                    </div>
                                  `).join('')}
                                </div>
                              </div>
                            `).join('')
                        }
                        </div>
                      `,
                      width: '90%',
                      confirmButtonText: 'Tutup',
                      confirmButtonColor: '#003087',
                    });
                  } catch (err) {
                    Swal.fire({
                      icon: 'error',
                      title: 'Gagal Membaca Data',
                      text: '⚠️ Format data di localStorage tidak valid.',
                      confirmButtonText: 'OK',
                      customClass: { confirmButton: 'btn btn-danger' },
                    });
                  }
                }}
                style={{
                  position: 'absolute',
                  top: 10,
                  left: 15,
                  background: 'transparent',
                  border: 'none',
                  color: '#fff',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  marginTop: 8,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  transition: 'transform 0.3s ease, color 0.3s ease',
                }}
                title="Lihat Data Local"
                onMouseEnter={e => {
                  e.currentTarget.style.color = '#00e6ff';
                  e.currentTarget.style.transform = 'scale(1.1)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.color = '#fff';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                <Database size={20} />
                <span>Log</span>
              </button>

              {/* Tombol Sinkronisasi (kanan atas) */}
              <button
                onClick={async () => {
                  try {
                    // 🔹 AMBIL SEMUA DATA YANG BELUM TERSINKRON DARI SEMUA TEMPAT MUNGKIN
                    const riwayat = JSON.parse(localStorage.getItem(LOCAL_RIWAYAT_KEY) || '[]');
                    const queueLama = JSON.parse(localStorage.getItem('absen_queue') || '[]'); // kalau pernah pakai

                    const dariRiwayat = riwayat.filter(item => !item.synced);
                    const dariQueue = Array.isArray(queueLama) ? queueLama : [];

                    // Gabungkan & hilangkan duplikat berdasarkan waktu + tanggal
                    const semuaUnsynced = [...dariRiwayat, ...dariQueue];
                    const uniqueUnsynced = Array.from(
                      new Map(
                        semuaUnsynced.map(item => [`${item.tanggal}-${item.waktu}`, item])
                      ).values()
                    );

                    if (uniqueUnsynced.length === 0) {
                      return Swal.fire({
                        icon: 'success',
                        title: 'Sudah Sinkron Semua',
                        text: 'Semua data absen sudah berhasil terupload ke server.',
                        timer: 3000,
                        showConfirmButton: false,
                      });
                    }

                    // 🔹 Swal loading dengan progress real-time
                    Swal.fire({
                      title: 'Sinkronisasi Data',
                      html: `Mengunggah <b>${uniqueUnsynced.length}</b> data tertunda...<br><small>Jangan tutup aplikasi</small>`,
                      allowOutsideClick: false,
                      allowEscapeKey: false,
                      didOpen: () => Swal.showLoading(),
                    });

                    let berhasil = 0;
                    let gagal = 0;

                    for (const item of uniqueUnsynced) {
                      const result = await syncToDatabase(item, item.waktu || item.currentTime);

                      if (result.success) {
                        berhasil++;
                        // Hapus dari queue lama kalau ada
                        if (localStorage.getItem('absen_queue')) {
                          const queueBaru = JSON.parse(localStorage.getItem('absen_queue') || '[]')
                            .filter(q => !(q.tanggal === item.tanggal && q.waktu === item.waktu));
                          localStorage.setItem('absen_queue', JSON.stringify(queueBaru));
                        }
                      } else {
                        gagal++;
                      }

                      // Update progress real-time
                      Swal.update({
                        title: `Proses: ${berhasil + gagal}/${uniqueUnsynced.length}`,
                        html: `
                          <div style="text-align:left; font-size:13px;">
                            ${result.success
                            ? `<span style="color:#4caf50">✓ ${item.waktu || '??:??'} - ${item.status || 'Absen'}</span>`
                            : `<span style="color:#f44336">✗ Gagal: ${item.waktu || '??:??'}</span>`
                          }
                          </div>
                          <br><b>${berhasil} berhasil</b> | <b>${gagal} gagal (akan otomatis retry)</b>
                        `,
                      });
                    }

                    // 🔹 HASIL AKHIR SESUAI KENYATAAN
                    if (gagal === 0) {
                      Swal.fire({
                        icon: 'success',
                        title: 'Semua Berhasil Tersinkron!',
                        text: `${berhasil} data telah berhasil diupload ke server.`,
                        timer: 4000,
                      });
                    } else if (berhasil === 0) {
                      Swal.fire({
                        icon: 'error',
                        title: 'Semua Gagal',
                        text: 'Tidak ada data yang berhasil diupload. Offline / Server Down. Coba lagi saat sudah normal ya!.',
                        timer: 5000,
                      });
                    } else {
                      Swal.fire({
                        icon: 'warning',
                        title: 'Sebagian Berhasil',
                        html: `
                          <b style="color:#4caf50">${berhasil} berhasil</b><br>
                          <b style="color:#f44336">${gagal} gagal</b> → akan dicoba lagi otomatis nanti
                        `,
                        timer: 6000,
                      });
                    }

                  } catch (err) {
                    console.error('Error fatal saat sync manual:', err);
                    Swal.fire({
                      icon: 'error',
                      title: 'Error Sistem',
                      text: 'Terjadi kesalahan tak terduga. Coba lagi nanti.',
                    });
                  }
                }}
                style={{
                  position: 'absolute',
                  top: 10,
                  right: 15,
                  background: 'transparent',
                  border: 'none',
                  color: '#fff',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  marginTop: 8,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  transition: 'transform 0.3s ease, color 0.3s ease',
                }}
                title="Sinkronisasi Data"
                onMouseEnter={e => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                  e.currentTarget.style.color = '#ffd700';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.color = '#fff';
                }}
              >
                <span>Sync</span>
                <i className="fas fa-sync-alt" style={{ fontSize: 18 }}></i>
              </button>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
              {/* REKAPAN — HANYA SEKALI */}
              {hasData && <RekapanCard items={allItems} />}

              {/* DAFTAR ABSEN HARIAN */}
              {hasData ? (
                Object.keys(groupedRiwayat)
                  .sort((a, b) => new Date(b) - new Date(a)) // Urutkan dari terbaru
                  .map(tanggal => (
                    <AbsenCard key={tanggal} items={groupedRiwayat[tanggal]} periode={getPeriod()} />
                  ))
              ) : (
                <div style={{
                  textAlign: 'center',
                  padding: '60px 20px',
                  color: '#6c757d',
                  fontStyle: 'italic',
                  fontSize: 15,
                }}>
                  Belum ada data absen untuk periode ini.
                </div>
              )}
            </div>
            <div style={{ padding: '16px 20px 20px' }}>
              <button
                onClick={() => setShowHistory(false)}
                style={{
                  width: '100%', padding: '14px',
                  background: 'linear-gradient(135deg, #dc3545, #c82333)',
                  color: '#fff', border: 'none', borderRadius: 14,
                  fontSize: 16, fontWeight: '600', cursor: 'pointer',
                }}
              >
                Tutup
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* MODAL ABSEN HARI INI */}
      {showTodayModal && todayAbsen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setShowTodayModal(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0, 0, 0, 0.55)',
            display: 'flex', justifyContent: 'center', alignItems: 'center',
            zIndex: 1000, padding: 20,
          }}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%', maxWidth: 440, background: '#ffffff',
              borderRadius: 24, boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
              overflow: 'hidden', fontFamily: "'Poppins', sans-serif",
              maxHeight: '90vh',
            }}
          >
            <div style={{
              background: 'linear-gradient(135deg, #003087, #0056b3)',
              color: '#fff', padding: '18px 24px', textAlign: 'center',
              fontSize: 18, fontWeight: '700',
            }}>
              Absen Hari Ini
            </div>

            <div style={{ padding: '20px' }}>
              <AbsenCard item={todayAbsen} />
            </div>

            <div style={{ padding: '0 20px 20px', textAlign: 'center' }}>
              <button
                onClick={() => setShowTodayModal(false)}
                style={{
                  width: '100%', padding: '14px',
                  background: 'linear-gradient(135deg, #dc3545, #c82333)',
                  color: '#fff', border: 'none', borderRadius: 14,
                  fontSize: 16, fontWeight: '600', cursor: 'pointer',
                }}
              >
                Tutup
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default Absen;
